//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import angular, {
  IHttpService,
  IPromise,
  IRootScopeService,
  IWindowService,
} from 'angular';
import _ from 'underscore';

import ExperienceContainerId from '@models/ExperienceContainerId';
import { crl8 } from 'app/curalate.globals';

export default class ExperiencesIntegrationsService {
  public static PREVIEW_IFRAME_ID: string = 'fanreel-preview-iframe';

  constructor(
    private $http: IHttpService,
    private $rootScope: IRootScopeService,
    private $window: IWindowService,
  ) {}

  public destroyAllExperiences(): Promise<any> {
    return this.$window.crl8.destroyAllExperiences();
  }

  public createExperience(layout: string, sourceId: string) {
    return this.$window.crl8.createExperience(layout, {
      api: { sourceId },
      displayType: 'Preview',
    });
  }

  public openModalInFrame(containerId: ExperienceContainerId) {
    /*
            Adds setTimeout to account for a race condition between loading the experience and programatically
            opening the modal. This may be accounted for in a future experience-framework update, but for now the
            issue only really exists in the self-serve editor.
        */
    return setTimeout(() => {
      this.getPreviewIframe().contentWindow.crl8.openModal(containerId, '', {
        defaultToFirstItem: true,
      });
    }, 2000);
  }

  public openUploaderInFrame(containerId: ExperienceContainerId) {
    /*
            Adds setTimeout to account for a race condition between loading the experience and programatically
            opening the modal. This may be accounted for in a future experience-framework update, but for now the
            issue only really exists in the self-serve editor.
        */
    return setTimeout(() => {
      this.getPreviewIframe().contentWindow.crl8.openUploader(containerId);
    }, 500);
  }

  public updateFrameExperiences(layout: string, options: any): Promise<any> {
    return this.getPreviewIframe().contentWindow.crl8.updateExperience(layout, {
      options,
    });
  }

  public destroyAllFrameExperiences(): Promise<any> {
    return this.getPreviewIframe().contentWindow.crl8.destroyAllExperiences();
  }

  public createFrameExperienceFromOptions(
    layout: string,
    options: any,
  ): Promise<any> {
    return this.getPreviewIframe().contentWindow.crl8.createExperience(
      layout,
      options,
    );
  }

  public async getFromFanreelApi(
    dataSource: string,
    labels: string[],
    limit: number,
  ): Promise<any> {
    const processedFilters = labels.map((label) => `label:${label}`);
    let url = `${crl8.session?.baseUrls.media_api}${dataSource}?skipCache=true&limit=${limit}`;

    if (processedFilters.length > 0) {
      url += '&filter=' + processedFilters.join(' or ');
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching data from Fanreel API:', error);
      return null;
    }
  }

  public getPreviewIframe(): any {
    return angular.element(
      `#${ExperiencesIntegrationsService.PREVIEW_IFRAME_ID}`,
    )[0];
  }
}

angular
  .module('curalate')
  .service('ExperiencesIntegrationsService', [
    '$http',
    '$rootScope',
    '$window',
    ExperiencesIntegrationsService,
  ]);
